import { useState } from 'react';

import { Breadcrumb } from '@components/breadcrumb';
import { ExpandableSearch } from '@components/expandable-search';
import { Header } from '@components/header';
import { IconCloudSync } from '@components/icons/cloud-sync';
import { IconDropdown } from '@components/icons/dropdown';
import { IconList } from '@components/icons/list';
import { IconUpload } from '@components/icons/upload';
import { List } from '@components/integrations/integrations-types';
import { ListTable, useChangeToManualModal } from '@components/integrations/lists';
import { useListSidebar, useSyncListSidebar } from '@components/integrations/lists';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { usePersistedTableStore } from '@components/table/table-store';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { useCurrentCompany, useTableView } from 'api/authentication/hooks';
import { useLists } from 'api/lists/hooks';

import { useImportListModal } from './import';
import { useOpenListFromUrl } from './use-open-list-from-url';

import { useDeleteListModal } from '../modals/list-delete';

export const CompanyLists = () => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');

  const queryString = toQueryString({
    search: search,
  });

  const { data, isLoading } = useLists(queryString);
  useOpenListFromUrl(data);

  if (isLoading) return <Spinner />;

  return (
    <>
      <Header
        actions={<Actions search={search} setSearch={setSearch} lists={data ?? []} />}
        breadcrumbs={<Breadcrumb items={[{ label: t('lists') }]} />}
      />
      <ListPageContent lists={data} search={search} />
    </>
  );
};

const ListPageContent = ({ search, lists }: { search: string; lists?: List[] }) => {
  const showSidebar = useListSidebar();
  const showDeleteModal = useDeleteListModal();
  const showChangeToManualModal = useChangeToManualModal();

  const { tableView, setTableView } = useTableView('lists');
  const tableStore = usePersistedTableStore({
    tableView,
    setTableView,
  });

  return (
    <ListTable
      lists={lists}
      search={search}
      onEditList={(list) => showSidebar({ listId: list.shortId })}
      onDeleteList={(list) => showDeleteModal({ list })}
      onChangeToManual={showChangeToManualModal}
      tableStore={tableStore}
    />
  );
};

const Actions = ({
  search,
  setSearch,
  lists,
}: {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  lists: List[];
}) => {
  const { t } = useScopedTranslate(locale);
  const company = useCurrentCompany();
  const showSidebar = useListSidebar();
  const showImportModal = useImportListModal();
  const showSyncListSidebar = useSyncListSidebar();

  const hasIntegrations = company?.hasIntegrations ?? false;

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearch} defaultValue={search} />
      <Menu
        buttonProps={{
          variant: 'primary',
          iconLeft: <IconList />,
          text: t('createList'),
          iconRight: <IconDropdown />,
        }}
      >
        <Menu.Item label={t('createNewList')} onSelect={() => showSidebar()} icon={<IconList />} />
        <Menu.Item
          label={t('importList')}
          onSelect={() => showImportModal({ lists })}
          icon={<IconUpload />}
        />
        <Menu.Item
          label={t('addSyncedList')}
          onSelect={() => showSyncListSidebar()}
          icon={<IconCloudSync />}
          isDisabled={!hasIntegrations}
          tooltip={!hasIntegrations ? t('noIntegrations') : undefined}
        />
      </Menu>
    </Stack>
  );
};

const locale = {
  en: {
    lists: 'Lists',
    createList: 'Create list',
    createNewList: 'Create a new list',
    importList: 'Import list • CSV',
    addSyncedList: 'Sync list • Integration',
    noIntegrations: 'This features requires active integrations to be set up',
  },
};
